import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'theme';

function systemTheme() {
  return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

/* Reads the theme the inline boot script already applied to <html>, keeps it
   in sync with localStorage, and follows the OS until the visitor picks one. */
export function useTheme() {
  const [theme, setTheme] = useState(() => {
    if (typeof document === 'undefined') return 'dark';
    return document.documentElement.getAttribute('data-theme') || systemTheme();
  });

  // Apply to the document + keep the browser chrome colour in step.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', theme === 'light' ? '#F9FAFC' : '#08080C');
  }, [theme]);

  // Colour transitions are enabled only after the first paint, so loading the
  // page doesn't animate every surface from the wrong colour.
  useEffect(() => {
    const id = requestAnimationFrame(() =>
      document.documentElement.classList.add('theme-ready')
    );
    return () => cancelAnimationFrame(id);
  }, []);

  // Track OS changes, but only while the visitor hasn't made a choice.
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: light)');
    if (!mq) return;

    const onChange = (e) => {
      let stored = null;
      try {
        stored = localStorage.getItem(STORAGE_KEY);
      } catch {
        /* storage blocked — fall through and follow the OS */
      }
      if (!stored) setTheme(e.matches ? 'light' : 'dark');
    };

    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  const toggle = useCallback(() => {
    setTheme((current) => {
      const next = current === 'light' ? 'dark' : 'light';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* private mode — the choice just won't persist */
      }
      return next;
    });
  }, []);

  return { theme, toggle };
}
